import React from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import { TabData } from '@/__core/types/TabData';

const DEFAULT_ADD_TAB_ID = 10;
const TAB_HEIGHT = 34;
const ADD_TAB_WIDTH = 44;

const defaultGetTabIcon = (tabType: string) => {
  switch (tabType) {
    case 'list':
      return <ViewListIcon />;
    case 'add':
      return <AddIcon />;
    case 'new-user':
    case 'new-customer':
    case 'new-order':
    case 'new-inventory-record':
      return <PersonIcon />;
    case 'update-user':
    case 'update-customer':
    case 'update-employee':
    case 'update-order':
    case 'update-inventory-record':
      return <EditIcon />;
    case 'new-employee':
      return <PersonIcon />;
    default:
      return <PersonIcon />;
  }
};

export interface AppTabsProps {
  tabs: TabData[];
  activeTab: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  onCloseTab: (tabId: number) => void;
  onAddTab: () => void;
  /** Tab id used for the "+" add button (default 10) */
  addTabId?: number;
  getTabIcon?: (tab: TabData) => React.ReactNode;
}

export default function AppTabs({
  tabs,
  activeTab,
  onTabChange,
  onCloseTab,
  onAddTab,
  addTabId = DEFAULT_ADD_TAB_ID,
  getTabIcon = (tab) => defaultGetTabIcon(tab.type),
}: AppTabsProps) {
  const isAddTab = (tab: TabData) => tab.id === addTabId || tab.type === 'add';

  return (
    <Box sx={{ flex: 1, pb: 0, display: 'flex', alignItems: 'flex-end' }}>
      <Tabs
        value={activeTab}
        onChange={onTabChange}
        sx={{
          minHeight: `${TAB_HEIGHT}px !important`,
          height: `${TAB_HEIGHT}px !important`,
          '& .MuiTabs-flexContainer': {
            alignItems: 'flex-end',
            minHeight: `${TAB_HEIGHT}px !important`,
            height: `${TAB_HEIGHT}px !important`,
          },
          '& .MuiTabs-indicator': {
            display: 'none',
          },
        }}
      >
        {tabs.map((tab) => {
          const addTab = isAddTab(tab);

          return (
            <Tab
              key={tab.id}
              onClick={
                addTab
                  ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onAddTab();
                    }
                  : undefined
              }
              sx={{
                backgroundColor: addTab ? '#10b981' : '#a3a4a5',
                color: 'white',
                minHeight: `${TAB_HEIGHT}px !important`,
                height: `${TAB_HEIGHT}px !important`,
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                margin: '0 1px',
                borderRadius: '3px 3px 0 0',
                padding: addTab ? 0 : '0 9px',
                minWidth: addTab ? `${ADD_TAB_WIDTH}px !important` : 'auto',
                maxWidth: addTab ? `${ADD_TAB_WIDTH}px` : '240px',
                cursor: addTab ? 'pointer' : 'default',
                marginBottom: 0,
                lineHeight: 1,
                '&:hover': {
                  color: 'white',
                  backgroundColor: addTab ? '#059669' : '#6b7280',
                },
                '&.Mui-selected': {
                  color: 'white',
                  fontWeight: 500,
                  backgroundColor: addTab ? '#059669' : '#4b5563',
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  },
                  '& .MuiIconButton-root': {
                    color: 'white',
                  },
                },
              }}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: addTab ? 'center' : 'flex-start',
                    gap: 0.5,
                    px: addTab ? 0 : 0.25,
                    minWidth: 0,
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    pr: tab.closable !== false && !addTab ? 2.5 : 0,
                  }}
                >
                  <Box
                    sx={{
                      color: 'inherit',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      '& svg': {
                        width: addTab ? '20px' : '18px',
                        height: addTab ? '20px' : '18px',
                      },
                    }}
                  >
                    {getTabIcon(tab)}
                  </Box>
                  {tab.title && (
                    <Typography
                      sx={{
                        fontWeight: 'inherit',
                        fontSize: '0.875rem',
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {tab.title}
                    </Typography>
                  )}
                  {tab.closable !== false && !addTab && (
                    <Box
                      component='span'
                      onClick={(e) => {
                        e.stopPropagation();
                        onCloseTab(tab.id);
                      }}
                      sx={{
                        color: 'inherit',
                        opacity: 0.8,
                        cursor: 'pointer',
                        flexShrink: 0,
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          opacity: 1,
                        },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: '0.55rem' }} />
                    </Box>
                  )}
                </Box>
              }
            />
          );
        })}
      </Tabs>
    </Box>
  );
}
